import type { DocumentoResumen, Filtros, ResultadoBusqueda } from '../api';
import { api } from '../api';
import { familiaTipo, fechaLarga, MESES, nombreTipo, nombreTipoPlural, numero } from '../formato';
import { Resaltado } from './Resaltado';

interface Props {
  filtros: Filtros;
  resultado: ResultadoBusqueda | null;
  cargando: boolean;
  error: string | null;
  re: RegExp | null;
  onAbrir: (d: DocumentoResumen) => void;
  onPagina: (p: number) => void;
  onReintentar: () => void;
}

/** Describe en una frase qué se está mostrando: "Decretos de septiembre de 2026 que mencionan «tasas»". */
export function describirBusqueda(f: Filtros, total: number): string {
  const sujeto = f.tipo ? nombreTipoPlural(f.tipo).toLowerCase() : total === 1 ? 'norma' : 'normas';
  let txt = `${numero(total)} ${f.tipo && total === 1 ? nombreTipo(f.tipo).toLowerCase() : sujeto}`;
  if (f.numero) txt += ` con el número ${numero(f.numero)}`;
  if (f.mes && f.anio) txt += ` de ${MESES[f.mes - 1]} de ${f.anio}`;
  else if (f.mes) txt += ` de ${MESES[f.mes - 1]} (todos los años)`;
  else if (f.anio) txt += ` de ${f.anio}`;
  if (f.q) txt += f.exacto ? ` que contienen «${f.q}»` : ` que mencionan «${f.q}»`;
  return txt;
}

function Paginacion({ r, onPagina }: { r: ResultadoBusqueda; onPagina: (p: number) => void }) {
  const paginas = Math.max(1, Math.ceil(r.total / r.porPagina));
  if (paginas <= 1) return null;
  return (
    <nav className="paginacion" aria-label="Páginas de resultados">
      <button className="boton" disabled={r.pagina <= 1} onClick={() => onPagina(r.pagina - 1)}>
        Anteriores
      </button>
      <span>
        Página {numero(r.pagina)} de {numero(paginas)}
      </span>
      <button className="boton" disabled={r.pagina >= paginas} onClick={() => onPagina(r.pagina + 1)}>
        Siguientes
      </button>
    </nav>
  );
}

export function Resultados({ filtros, resultado, cargando, error, re, onAbrir, onPagina, onReintentar }: Props) {
  if (error) {
    return (
      <section className="resultados">
        <div className="aviso aviso--error" role="alert">
          <p>No se pudo hacer la búsqueda: {error}</p>
          <button className="boton" onClick={onReintentar}>
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  if (!resultado) {
    return (
      <section className="resultados" aria-busy="true">
        <p className="resultados__resumen resultados__resumen--cargando">Buscando…</p>
      </section>
    );
  }

  return (
    <section className="resultados" aria-busy={cargando} aria-live="polite">
      <p className="resultados__resumen">
        {describirBusqueda(filtros, resultado.total)}
        {resultado.total > 0 && ', de la más reciente a la más antigua'}
      </p>

      {resultado.total === 0 ? (
        <div className="aviso">
          <p>No hay normas que coincidan con todos los filtros.</p>
          <p>Pruebe con menos palabras, otro año o «Todas» en tipo de norma.</p>
        </div>
      ) : (
        <ol className={`lista ${cargando ? 'lista--cargando' : ''}`}>
          {resultado.data.map((d) => (
            <li key={d.nroInterno} className={`norma norma--${familiaTipo(d.tipo)}`}>
              <div className="norma__cabeza">
                <h3 className="norma__cita">
                  <span className="insignia">{nombreTipo(d.tipo)}</span>{' '}
                  <button className="norma__abrir" onClick={() => onAbrir(d)}>
                    N.º {numero(d.numero)}/{d.anio}
                  </button>
                </h3>
                <time className="norma__fecha" dateTime={d.fecha ?? undefined}>
                  {fechaLarga(d.fecha)}
                </time>
              </div>
              {d.visto && (
                <p className="norma__visto">
                  <Resaltado texto={d.visto} re={re} />
                </p>
              )}
              <div className="norma__acciones">
                <button className="enlace" onClick={() => onAbrir(d)}>
                  Leer la norma
                </button>
                <a className="enlace" href={api.urlPdf(d.nroInterno)} target="_blank" rel="noopener">
                  Abrir PDF
                </a>
              </div>
            </li>
          ))}
        </ol>
      )}

      <Paginacion r={resultado} onPagina={onPagina} />
    </section>
  );
}
